import { useCallback, useState } from 'react';
import { format } from 'date-fns';
import { useGlobalToast } from '@repo/atomic-components';
import { isEndpointLockError, usePostClusterDateUpdateMutation } from '@/api';
import { popBusyTask, pushBusyTask, selectSharedDataBundleId, useAppDispatch, useAppSelector } from '@/store';

interface EditClusterDateState {
  /**
   * Bumped on every open. The dialog seeds its calendar from `initialDate` at
   * mount, so call sites key on this to force a remount when the dialog is
   * re-opened for a different cluster while already showing — otherwise it
   * keeps the previous cluster's date while pointing at the new section.
   */
  openId: number;
  sectionIds: string[];
  initialDate: Date;
}

/**
 * Shared "Edit date" flow for clusters. Drives the sections-tree context menu
 * item and the thumbnails-bar date pencil, so both open the same dialog and go
 * through the same POSTs, busy strip and result toast.
 */
export const useEditClusterDate = () => {
  const dispatch = useAppDispatch();
  const toast = useGlobalToast();
  const bundleId = useAppSelector(selectSharedDataBundleId);
  const [updateClusterDate] = usePostClusterDateUpdateMutation();

  const [editState, setEditState] = useState<EditClusterDateState | null>(null);

  /**
   * `seedDateText` is the cluster's current label — the picker opens on that
   * date when it parses, otherwise on today. Applies uniformly to every target
   * when multiple clusters are being edited.
   */
  const openForClusters = useCallback((sectionIds: string[], seedDateText?: string | null) => {
    if (!sectionIds.length) return;
    const parsedDate = seedDateText ? new Date(seedDateText) : null;
    setEditState((current) => ({
      openId: (current?.openId ?? 0) + 1,
      sectionIds,
      initialDate: parsedDate && !isNaN(parsedDate.getTime()) ? parsedDate : new Date(),
    }));
  }, []);

  const close = useCallback(() => setEditState(null), []);

  const confirm = useCallback(
    async (date: Date) => {
      if (!editState || !bundleId) return;
      const dateString = format(date, 'yyyy-MM-dd'); // Format as YYYY-MM-DD
      const sectionIds = [...editState.sectionIds];
      const count = sectionIds.length;

      // Close the dialog straight away — the actions-panel busy strip already
      // shows the spinner while the sequential POSTs run in the background,
      // so making the user stare at a frozen modal adds nothing.
      setEditState(null);

      const busyTaskId = `update-cluster-date-${Date.now()}`;
      dispatch(
        pushBusyTask({
          id: busyTaskId,
          description: count === 1 ? 'Updating cluster date...' : `Updating ${count} cluster dates...`,
        }),
      );
      try {
        // Fire sequentially, not in parallel: the backend resorts clusters into
        // date order per call, so parallel requests race — the second request's
        // resort doesn't see the first's update yet and the final order comes
        // out wrong. Awaiting one at a time keeps each backend resort building
        // on the previous update. Individual failures don't abort the batch —
        // we count outcomes so the toast can distinguish full success, partial,
        // and full failure instead of vaguely saying "one or more" failed.
        let succeeded = 0;
        let failed = 0;
        let isRefusedByLock = false;
        for (const sectionId of sectionIds) {
          try {
            await updateClusterDate({
              sectionId,
              bundleId,
              date: dateString,
            }).unwrap();
            succeeded++;
          } catch (error) {
            // A lock refusal does end the batch, as the lock would refuse every section left
            if (isEndpointLockError(error)) {
              isRefusedByLock = true;
              failed = count - succeeded;
              break;
            }
            failed++;
          }
        }

        // The lock's own toast already explains why nothing was updated
        if (isRefusedByLock && succeeded === 0) return;

        if (failed === 0) {
          toast.current?.show({
            severity: 'success',
            summary: 'Date Updated',
            detail:
              count === 1
                ? 'Cluster date has been updated successfully.'
                : `${count} cluster dates have been updated successfully.`,
            life: 3000,
          });
        } else if (succeeded === 0) {
          toast.current?.show({
            severity: 'error',
            summary: 'Update Failed',
            detail:
              count === 1
                ? 'Failed to update cluster date. Please try again.'
                : `Failed to update ${count} cluster dates. Please try again.`,
            life: 3000,
          });
        } else {
          toast.current?.show({
            severity: 'warn',
            summary: 'Partially Updated',
            detail: `Updated ${succeeded} of ${count} cluster dates, ${failed} failed.`,
            life: 5000,
          });
        }
      } finally {
        dispatch(popBusyTask(busyTaskId));
      }
    },
    [bundleId, dispatch, editState, toast, updateClusterDate],
  );

  return {
    /** Null while the dialog is closed. */
    editState,
    openForClusters,
    close,
    confirm,
  };
};
